import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import userApi from '../../api/userApi';
import { useToast } from '../../contexts/ToastContext';
import RolesMultiSelect from '../../components/RolesMultiSelect';

const AddUser = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    roles: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRoleChange = (selectedOptions) => {
    const selectedRoleIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData(prev => ({
      ...prev,
      roles: selectedRoleIds,
    }));
    if (validationErrors.roles) {
      setValidationErrors(prev => ({ ...prev, roles: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationErrors({});

    try {
      const payload = {
        ...formData,
        roles: formData.roles.map(id => parseInt(id, 10)),
      };
      await userApi.createUser(payload);
      addToast('User created successfully!', 'success');
      navigate('/users');
    } catch (err) {
      console.error('Failed to add User:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to add User. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Add New User</h1>
      {loading ? (
        <SkeletonLoader type="form" />
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              label="First Name"
              name="first_name"
              id="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required={true}
              placeholder="First Name"
              error={validationErrors?.first_name?.[0]}
            />
            <TextInput
              label="Last Name"
              name="last_name"
              id="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required={true}
              placeholder="Last Name"
              error={validationErrors?.last_name?.[0]}
            />
            <TextInput
              label="Email"
              name="email"
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required={true}
              placeholder="Email"
              error={validationErrors?.email?.[0]}
            />
            <RolesMultiSelect
              label="Roles"
              value={formData.roles}
              onChange={handleRoleChange}
              error={validationErrors?.roles?.[0]}
              required={true}
            />
            <TextInput
              label="Password"
              name="password"
              id="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required={true}
              placeholder="Password"
              error={validationErrors?.password?.[0]}
            />
          </div>
          <div className="flex items-center justify-between mt-6">
            <Button
              type="submit"
              className="bg-accent-red hover:bg-red-700 text-white"
              loading={loading}
            >
              Add User
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/users')}
              className="bg-gray-500 hover:bg-gray-600 text-white"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddUser;